// Procedural pixel art for Turkey Hunt: the farm scenery split into layers
// (background, barn front, fence, tree, hay, foreground grass) plus the turkey
// sprite frames. Built once per page and cached.

import { H, W, alphaMask, dither, ellipse, makeLayer, noise, poly, px, rect, threshold, type Layer } from "./pixel";

export interface Geometry {
  barn: { x0: number; x1: number; wallTop: number; bottom: number; door: { x0: number; x1: number; y0: number }; loft: { x0: number; x1: number; y0: number; y1: number } };
  trunk: { x0: number; x1: number; bottom: number };
  fence: { x0: number; x1: number; top: number; board: number; bottom: number };
  hay: { x: number; y: number; w: number; h: number }[];
  windmillHub: [number, number];
  lake: { x0: number; x1: number; y0: number; y1: number };
}

export const GEO: Geometry = {
  barn: { x0: 346, x1: 446, wallTop: 120, bottom: 178, door: { x0: 380, x1: 412, y0: 142 }, loft: { x0: 389, x1: 403, y0: 102, y1: 114 } },
  trunk: { x0: 32, x1: 64, bottom: 216 },
  fence: { x0: 92, x1: 340, top: 168, board: 186, bottom: 196 },
  hay: [
    { x: 138, y: 190, w: 38, h: 24 },
    { x: 262, y: 194, w: 36, h: 22 },
    { x: 416, y: 188, w: 32, h: 22 },
  ],
  windmillHub: [463, 104],
  lake: { x0: 150, x1: 330, y0: 142, y1: 158 },
};

export interface Scenery {
  bg: HTMLCanvasElement;
  barnFront: HTMLCanvasElement;
  fence: HTMLCanvasElement;
  tree: HTMLCanvasElement;
  hay: HTMLCanvasElement;
  fg: HTMLCanvasElement;
  /** Occluders in front of far-plane (A) turkeys. */
  maskA: Uint8Array;
  /** Occluders in front of near-plane (B) turkeys. */
  maskB: Uint8Array;
  clouds: HTMLCanvasElement[];
}

const SKY = ["#5fb4f0", "#74c1f2", "#8bcdf3", "#a3d8f3", "#bfe4f2", "#d9eef0"];

function drawSky(ctx: CanvasRenderingContext2D) {
  const bandH = 150 / SKY.length;
  for (let y = 0; y < 150; y++) {
    const f = y / bandH;
    const i = Math.min(SKY.length - 1, Math.floor(f));
    const t = f - i;
    for (let x = 0; x < W; x++) {
      const next = SKY[Math.min(SKY.length - 1, i + 1)];
      px(ctx, x, y, dither(x, y, t * t) ? next : SKY[i]);
    }
  }
  // Sun with a pixel halo.
  ellipse(ctx, 420, 62, 15, 15, "#fff6c4");
  ellipse(ctx, 420, 62, 11, 11, "#ffe78a");
  ellipse(ctx, 420, 62, 8, 8, "#ffd75a");
}

function ridge(ctx: CanvasRenderingContext2D, base: number, amp: number, seed: number, col: string, snow?: string, shade?: string) {
  for (let x = 0; x < W; x++) {
    const h =
      Math.sin(x * 0.021 + seed) * amp * 0.6 +
      Math.sin(x * 0.047 + seed * 2.1) * amp * 0.3 +
      Math.sin(x * 0.11 + seed * 0.7) * amp * 0.1 +
      amp;
    const top = Math.round(base - h);
    for (let y = top; y < base + 30; y++) {
      let c = col;
      if (snow && y < top + 4 + (noise(x, 0, seed) > 0.5 ? 1 : 0) && h > amp * 1.25) c = snow;
      else if (shade && (x + y) % 7 === 0 && y > top + 3) c = shade;
      px(ctx, x, y, c);
    }
  }
}

function drawSilo(ctx: CanvasRenderingContext2D) {
  const x0 = 326;
  const x1 = 346;
  ellipse(ctx, (x0 + x1) / 2, 98, 10, 8, "#8b9db0");
  ellipse(ctx, (x0 + x1) / 2 - 2, 96, 5, 4, "#b8c6d4");
  for (let y = 98; y < 178; y++) {
    for (let x = x0; x < x1; x++) {
      const edge = x - x0;
      let c = edge < 3 ? "#cdd8e2" : edge > 15 ? "#7d8fa3" : "#a7b6c6";
      if (y % 14 === 0) c = "#6d7f93";
      px(ctx, x, y, c);
    }
  }
}

function drawWindmill(ctx: CanvasRenderingContext2D) {
  // Lattice tower; blades are animated by the engine.
  for (let y = 104; y < 172; y++) {
    const spread = Math.round((y - 104) * 0.13);
    px(ctx, 463 - 2 - spread, y, "#6b5a4a");
    px(ctx, 463 + 2 + spread, y, "#6b5a4a");
    if (y % 9 === 0) for (let x = 463 - 2 - spread; x <= 463 + 2 + spread; x++) px(ctx, x, y, "#7d6b59");
  }
  rect(ctx, 459, 100, 9, 6, "#8a7560");
}

function drawField(ctx: CanvasRenderingContext2D) {
  // Far meadow, lake, then nearer rolling field.
  for (let y = 140; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const hill = Math.sin(x * 0.018 + 1.3) * 4 + Math.sin(x * 0.05) * 1.5;
      const yy = y + hill;
      let c: string;
      if (yy < 152) c = "#7cb85a";
      else if (yy < 168) c = dither(x, y, (yy - 152) / 16) ? "#6aab4a" : "#7cb85a";
      else if (yy < 196) c = dither(x, y, (yy - 168) / 28) ? "#5c9d3f" : "#6aab4a";
      else c = dither(x, y, (yy - 196) / 40) ? "#4f8e37" : "#5c9d3f";
      if (noise(x, y, 3) > 0.985) c = "#8fca66";
      px(ctx, x, y, c);
    }
  }
  // Lake.
  const L = GEO.lake;
  const cx = (L.x0 + L.x1) / 2;
  const cy = (L.y0 + L.y1) / 2;
  ellipse(ctx, cx, cy, (L.x1 - L.x0) / 2 + 2, (L.y1 - L.y0) / 2 + 2, "#c9b98a");
  ellipse(ctx, cx, cy, (L.x1 - L.x0) / 2, (L.y1 - L.y0) / 2, "#3f8fc8");
  ellipse(ctx, cx, cy - 2, (L.x1 - L.x0) / 2 - 8, (L.y1 - L.y0) / 2 - 4, "#56a6db");
  // A dirt path winding to the barn.
  for (let y = 178; y < H; y++) {
    const t = (y - 178) / (H - 178);
    const xc = 396 - t * 70 + Math.sin(t * 5) * 6;
    const hw = 6 + t * 20;
    for (let x = Math.round(xc - hw); x < xc + hw; x++) px(ctx, x, y, noise(x, y, 9) > 0.8 ? "#b99568" : "#c9a676");
  }
  // Wild flowers.
  for (let i = 0; i < 70; i++) {
    const x = Math.floor(noise(i, 1, 11) * W);
    const y = 158 + Math.floor(noise(i, 2, 11) * 100);
    const col = ["#ffffff", "#ffd84d", "#ff8fb3", "#c9a2ff"][i % 4];
    px(ctx, x, y, col);
  }
}

function drawBarnInterior(ctx: CanvasRenderingContext2D) {
  const B = GEO.barn;
  rect(ctx, B.door.x0, B.door.y0, B.door.x1 - B.door.x0, B.bottom - B.door.y0, "#2a1810");
  rect(ctx, B.door.x0, B.bottom - 6, B.door.x1 - B.door.x0, 6, "#5b3a1e");
  for (let x = B.door.x0 + 2; x < B.door.x1 - 2; x += 3) px(ctx, x, B.bottom - 7 - (x % 2), "#c9a24a");
  rect(ctx, B.loft.x0, B.loft.y0, B.loft.x1 - B.loft.x0, B.loft.y1 - B.loft.y0, "#24140c");
}

function buildBackground(): HTMLCanvasElement {
  const L = makeLayer(W, H);
  const ctx = L.ctx;
  drawSky(ctx);
  ridge(ctx, 128, 26, 1.7, "#9db3d6", "#f2f6fb", "#91a8cd");
  ridge(ctx, 140, 16, 4.2, "#7f9fc6", undefined, "#7696bd");
  ridge(ctx, 150, 9, 2.4, "#6f9a7a", undefined, "#668f71");
  drawField(ctx);
  drawSilo(ctx);
  drawWindmill(ctx);
  drawBarnInterior(ctx);
  return L.canvas;
}

function buildBarnFront(): Layer {
  const L = makeLayer(W, H);
  const ctx = L.ctx;
  const B = GEO.barn;
  const mid = (B.x0 + B.x1) / 2;
  // Walls with vertical planks.
  for (let y = B.wallTop; y < B.bottom; y++) {
    for (let x = B.x0; x < B.x1; x++) {
      let c = (x - B.x0) % 6 === 0 ? "#9c2b26" : "#c23a2f";
      if (noise(x, y, 21) > 0.93) c = "#b0332a";
      px(ctx, x, y, c);
    }
  }
  // Gable.
  for (let y = 92; y < B.wallTop; y++) {
    const hw = ((y - 92) / (B.wallTop - 92)) * ((B.x1 - B.x0) / 2);
    for (let x = Math.round(mid - hw); x < mid + hw; x++) px(ctx, x, y, (x - B.x0) % 6 === 0 ? "#9c2b26" : "#c23a2f");
  }
  // Roof.
  for (let i = 0; i < 6; i++) {
    poly(ctx, [[mid, 84 + i], [B.x0 - 6, B.wallTop + i - 2], [B.x0 - 6, B.wallTop + i - 1], [mid, 85 + i]], i < 2 ? "#5a4038" : "#3f2d28");
    poly(ctx, [[mid, 84 + i], [B.x1 + 6, B.wallTop + i - 2], [B.x1 + 6, B.wallTop + i - 1], [mid, 85 + i]], i < 2 ? "#5a4038" : "#3f2d28");
  }
  // Holes: door and loft.
  ctx.clearRect(B.door.x0, B.door.y0, B.door.x1 - B.door.x0, B.bottom - B.door.y0);
  ctx.clearRect(B.loft.x0, B.loft.y0, B.loft.x1 - B.loft.x0, B.loft.y1 - B.loft.y0);
  // White trim and the classic X on the side panels.
  const trim = "#f3efe4";
  rect(ctx, B.door.x0 - 2, B.door.y0 - 2, B.door.x1 - B.door.x0 + 4, 2, trim);
  rect(ctx, B.door.x0 - 2, B.door.y0, 2, B.bottom - B.door.y0, trim);
  rect(ctx, B.door.x1, B.door.y0, 2, B.bottom - B.door.y0, trim);
  rect(ctx, B.loft.x0 - 1, B.loft.y0 - 1, B.loft.x1 - B.loft.x0 + 2, 1, trim);
  rect(ctx, B.loft.x0 - 1, B.loft.y1, B.loft.x1 - B.loft.x0 + 2, 1, trim);
  rect(ctx, B.loft.x0 - 1, B.loft.y0, 1, B.loft.y1 - B.loft.y0, trim);
  rect(ctx, B.loft.x1, B.loft.y0, 1, B.loft.y1 - B.loft.y0, trim);
  for (const [x0, x1] of [[B.x0 + 6, B.door.x0 - 8], [B.door.x1 + 8, B.x1 - 6]]) {
    const y0 = 146;
    const y1 = B.bottom - 2;
    rect(ctx, x0, y0, x1 - x0, 1, trim);
    rect(ctx, x0, y1, x1 - x0, 1, trim);
    rect(ctx, x0, y0, 1, y1 - y0, trim);
    rect(ctx, x1, y0, 1, y1 - y0 + 1, trim);
    for (let t = 0; t <= 1; t += 0.02) {
      px(ctx, x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, trim);
      px(ctx, x1 - (x1 - x0) * t, y0 + (y1 - y0) * t, trim);
    }
  }
  rect(ctx, B.x0, B.wallTop, B.x1 - B.x0, 1, trim);
  rect(ctx, B.x0, B.bottom - 1, B.x1 - B.x0, 2, "#6b231c");
  threshold(L);
  return L;
}

function buildFence(): Layer {
  const L = makeLayer(W, H);
  const ctx = L.ctx;
  const F = GEO.fence;
  const wood = "#d9b27c";
  const dark = "#a07a4c";
  // Two rails and a bottom board.
  for (const [y, h] of [[F.top + 6, 4], [F.top + 13, 4], [F.board, F.bottom - F.board]] as [number, number][]) {
    rect(ctx, F.x0, y, F.x1 - F.x0, h, wood);
    rect(ctx, F.x0, y + h - 1, F.x1 - F.x0, 1, dark);
    for (let x = F.x0; x < F.x1; x += 5) if (noise(x, y, 5) > 0.6) px(ctx, x, y + 1, "#c79d66");
  }
  for (let x = F.x0; x <= F.x1; x += 20) {
    rect(ctx, x, F.top, 4, F.bottom - F.top + 2, wood);
    rect(ctx, x + 3, F.top + 1, 1, F.bottom - F.top + 1, dark);
    rect(ctx, x, F.top, 4, 1, "#f0d2a2");
  }
  return L;
}

function buildTree(): Layer {
  const L = makeLayer(W, H);
  const ctx = L.ctx;
  const T = GEO.trunk;
  // Trunk with roots and bark texture.
  for (let y = 92; y < T.bottom; y++) {
    const flare = y > T.bottom - 14 ? Math.round((y - (T.bottom - 14)) * 0.8) : 0;
    for (let x = T.x0 - flare; x < T.x1 + flare; x++) {
      const rel = (x - T.x0) / (T.x1 - T.x0);
      let c = rel < 0.25 ? "#8a5b34" : rel > 0.75 ? "#4f3018" : "#6b4224";
      if (noise(x, Math.floor(y / 3), 31) > 0.82) c = "#3f2512";
      px(ctx, x, y, c);
    }
  }
  // Canopy: clusters with a lit top-left and leaf speckle.
  const blobs: [number, number, number, number][] = [
    [52, 58, 50, 36], [22, 86, 30, 24], [92, 80, 34, 26], [60, 26, 36, 26], [106, 52, 24, 20], [8, 50, 20, 22], [70, 100, 30, 16],
  ];
  for (const [cx, cy, rx, ry] of blobs) ellipse(ctx, cx, cy, rx, ry, "#2f6b2a");
  for (const [cx, cy, rx, ry] of blobs) ellipse(ctx, cx - rx * 0.12, cy - ry * 0.16, rx * 0.82, ry * 0.76, "#3d8435");
  for (const [cx, cy, rx, ry] of blobs) ellipse(ctx, cx - rx * 0.28, cy - ry * 0.34, rx * 0.46, ry * 0.4, "#55a243");
  const d = ctx.getImageData(0, 0, W, H);
  for (let y = 0; y < 130; y++) {
    for (let x = 0; x < 150; x++) {
      const i = (y * W + x) * 4;
      if (d.data[i + 3] === 0) continue;
      const n = noise(x, y, 41);
      if (n > 0.9) { d.data[i] = 0x23; d.data[i + 1] = 0x52; d.data[i + 2] = 0x20; }
      else if (n < 0.06) { d.data[i] = 0x74; d.data[i + 1] = 0xbf; d.data[i + 2] = 0x56; }
    }
  }
  ctx.putImageData(d, 0, 0);
  threshold(L);
  return L;
}

function buildHay(): Layer {
  const L = makeLayer(W, H);
  const ctx = L.ctx;
  for (const b of GEO.hay) {
    rect(ctx, b.x, b.y, b.w, b.h, "#e2b84f");
    rect(ctx, b.x, b.y, b.w, 2, "#f2d47c");
    rect(ctx, b.x, b.y + b.h - 2, b.w, 2, "#b98a2c");
    for (let y = b.y + 2; y < b.y + b.h - 2; y++)
      for (let x = b.x; x < b.x + b.w; x++) if (noise(x, y, 51) > 0.78) px(ctx, x, y, noise(x, y, 52) > 0.5 ? "#c99a36" : "#f5d98a");
    // Twine.
    rect(ctx, b.x + Math.round(b.w * 0.3), b.y, 1, b.h, "#8a5a2a");
    rect(ctx, b.x + Math.round(b.w * 0.7), b.y, 1, b.h, "#8a5a2a");
    // Stray straw on top.
    for (let i = 0; i < 6; i++) px(ctx, b.x + 3 + i * 6, b.y - 1, "#f2d47c");
  }
  return L;
}

function buildForeground(): Layer {
  const L = makeLayer(W, H);
  const ctx = L.ctx;
  for (let x = 0; x < W; x++) {
    const tuft = Math.round(4 + Math.sin(x * 0.31) * 2 + noise(x, 0, 61) * 5 + Math.sin(x * 0.07) * 2);
    const top = 220 - tuft;
    for (let y = top; y < H; y++) {
      let c = y < top + 3 ? "#5fae45" : y < top + 8 ? "#4a943a" : "#3e8331";
      if (noise(x, y, 62) > 0.93) c = "#6fbf52";
      px(ctx, x, y, c);
    }
  }
  return L;
}

function buildCloud(seed: number): HTMLCanvasElement {
  const L = makeLayer(64, 26);
  const n = 4 + Math.floor(noise(seed, 0, 71) * 3);
  for (let i = 0; i < n; i++) {
    const cx = 12 + i * (40 / n) + noise(seed, i, 72) * 6;
    const cy = 15 - noise(seed, i, 73) * 5;
    const r = 7 + noise(seed, i, 74) * 5;
    ellipse(L.ctx, cx, cy, r, r * 0.8, "#ffffff");
  }
  // Soft bluish underside.
  const d = L.ctx.getImageData(0, 0, 64, 26);
  for (let y = 17; y < 26; y++)
    for (let x = 0; x < 64; x++) {
      const i = (y * 64 + x) * 4;
      if (d.data[i + 3] && dither(x, y, (y - 17) / 8)) { d.data[i] = 0xdc; d.data[i + 1] = 0xea; d.data[i + 2] = 0xf6; }
    }
  L.ctx.putImageData(d, 0, 0);
  return L.canvas;
}

let cachedScenery: Scenery | null = null;

export function getScenery(): Scenery {
  if (cachedScenery) return cachedScenery;
  const barnFront = buildBarnFront();
  const fence = buildFence();
  const tree = buildTree();
  const hay = buildHay();
  const fg = buildForeground();
  const mBarn = alphaMask(barnFront);
  const mFence = alphaMask(fence);
  const mTree = alphaMask(tree);
  const mHay = alphaMask(hay);
  const mFg = alphaMask(fg);
  const maskB = new Uint8Array(W * H);
  const maskA = new Uint8Array(W * H);
  for (let i = 0; i < maskA.length; i++) {
    maskB[i] = mTree[i] | mHay[i] | mFg[i];
    maskA[i] = maskB[i] | mBarn[i] | mFence[i];
  }
  cachedScenery = {
    bg: buildBackground(),
    barnFront: barnFront.canvas,
    fence: fence.canvas,
    tree: tree.canvas,
    hay: hay.canvas,
    fg: fg.canvas,
    maskA,
    maskB,
    clouds: [1, 2, 3, 4].map(buildCloud),
  };
  return cachedScenery;
}

// ---------------------------------------------------------------------------
// Turkey sprites. Frame size 32x30, facing right, anchor = feet (16, 29).
// ---------------------------------------------------------------------------

export const TK_W = 32;
export const TK_H = 30;
export const TK_AX = 16;
export const TK_AY = 29;
/** Body badge centre relative to the anchor, facing right. */
export const TK_BADGE: [number, number] = [-1, -11];
/** Head centre relative to the anchor, facing right. */
export const TK_HEAD: [number, number] = [6, -21];

export interface TurkeySprite {
  canvas: HTMLCanvasElement;
  alpha: Uint8Array;
}

export interface TurkeyFrames {
  stand: TurkeySprite;
  blink: TurkeySprite;
  peck: TurkeySprite;
  run: TurkeySprite[];
  fly: TurkeySprite[];
  dizzy: TurkeySprite;
}

type Legs = "stand" | "run0" | "run1" | "run2" | "run3" | "tuck";

function drawTurkey(opts: { legs: Legs; eye: "open" | "closed" | "x"; tail: "fan" | "fold"; wing: "fold" | "up" | "mid" | "down"; headDy?: number }): TurkeySprite {
  const L = makeLayer(TK_W, TK_H);
  const ctx = L.ctx;
  const hd = opts.headDy ?? 0;

  // Tail fan (upper half circle) or folded tail for flight.
  if (opts.tail === "fan") {
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, TK_W, 21);
    ctx.clip();
    ellipse(ctx, 11, 17, 12, 13, "#f1d9a6");
    ellipse(ctx, 11, 17, 11, 12, "#6b3a1e");
    ellipse(ctx, 11, 17, 8.5, 9.5, "#a85a2c");
    ellipse(ctx, 11, 17, 6, 7, "#d4883e");
    ellipse(ctx, 11, 17, 3.5, 4.5, "#7a3e1c");
    ctx.restore();
    // Feather separators.
    for (let a = 0; a < 7; a++) {
      const ang = Math.PI + (a / 6) * Math.PI;
      for (let r = 5; r < 12; r++) px(ctx, 11 + Math.cos(ang) * r, 17 + Math.sin(ang) * r * 1.08, "#4e2a14");
    }
  } else {
    poly(ctx, [[3, 13], [10, 15], [10, 20], [2, 18]], "#6b3a1e");
    poly(ctx, [[2, 13], [4, 13], [3, 18], [1, 18]], "#f1d9a6");
  }

  // Legs.
  const leg = "#e39b2f";
  const legPos: Record<Legs, [number, number, number, number]> = {
    stand: [13, 0, 17, 0],
    run0: [12, -1, 18, 1],
    run1: [14, 0, 16, 0],
    run2: [18, 1, 12, -1],
    run3: [16, 0, 14, 0],
    tuck: [14, 0, 17, 0],
  };
  const [l1, d1, l2, d2] = legPos[opts.legs];
  if (opts.legs === "tuck") {
    rect(ctx, 13, 24, 4, 1, leg);
    rect(ctx, 16, 25, 3, 1, leg);
  } else {
    for (const [lx, dx] of [[l1, d1], [l2, d2]] as [number, number][]) {
      rect(ctx, lx, 24, 1, 4, leg);
      rect(ctx, lx + dx - 1, 28, 3, 1, leg);
      px(ctx, lx + dx + 2, 28, "#c27a18");
    }
  }

  // Body.
  ellipse(ctx, 15, 18, 8, 7, "#4a2a17");
  ellipse(ctx, 14, 16, 6, 4, "#6e3f22");
  for (let i = 0; i < 9; i++) px(ctx, 10 + ((i * 3) % 10), 15 + ((i * 5) % 8), "#2e1a0e");
  for (let x = 9; x < 22; x += 2) px(ctx, x, 23, "#2e1a0e");

  // Wing.
  if (opts.wing === "fold") {
    ellipse(ctx, 13, 18, 5.5, 4, "#5b341c");
    for (let x = 10; x < 18; x += 2) px(ctx, x, 19, "#8a5a32");
    px(ctx, 9, 20, "#f1d9a6");
  } else {
    const tips: Record<string, [number, number][]> = {
      up: [[10, 15], [3, 2], [8, 3], [18, 15]],
      mid: [[10, 16], [0, 11], [4, 14], [18, 17]],
      down: [[10, 17], [4, 27], [9, 26], [18, 18]],
    };
    poly(ctx, tips[opts.wing], "#5b341c");
    const t = tips[opts.wing];
    poly(ctx, [t[1], t[2], [(t[1][0] + t[2][0]) / 2 + 1, (t[1][1] + t[2][1]) / 2 + (opts.wing === "down" ? -3 : 3)]], "#f1d9a6");
  }

  // Neck and head.
  rect(ctx, 20, 9 + hd, 3, 7 - hd, "#8fa7c4");
  ellipse(ctx, 22.5, 8 + hd, 3.2, 3, "#a9c0dc");
  px(ctx, 21, 7 + hd, "#c7d7ea");
  if (opts.eye === "open") {
    px(ctx, 23, 7 + hd, "#141414");
    px(ctx, 22, 6 + hd, "#ffffff");
  } else if (opts.eye === "closed") {
    rect(ctx, 22, 7 + hd, 2, 1, "#5b6f88");
  } else {
    px(ctx, 22, 6 + hd, "#141414");
    px(ctx, 24, 6 + hd, "#141414");
    px(ctx, 23, 7 + hd, "#141414");
    px(ctx, 22, 8 + hd, "#141414");
    px(ctx, 24, 8 + hd, "#141414");
  }
  // Beak, snood and wattle.
  rect(ctx, 25, 8 + hd, 2, 1, "#f2b632");
  px(ctx, 25, 9 + hd, "#d48f1a");
  px(ctx, 25, 6 + hd, "#d8323a");
  px(ctx, 26, 7 + hd, "#d8323a");
  rect(ctx, 23, 10 + hd, 2, 3, "#d8323a");
  px(ctx, 24, 13 + hd, "#b8222c");

  threshold(L);
  const d = ctx.getImageData(0, 0, TK_W, TK_H).data;
  const alpha = new Uint8Array(TK_W * TK_H);
  for (let i = 0; i < alpha.length; i++) alpha[i] = d[i * 4 + 3] ? 1 : 0;
  return { canvas: L.canvas, alpha };
}

let cachedTurkey: TurkeyFrames | null = null;

export function getTurkeyFrames(): TurkeyFrames {
  if (cachedTurkey) return cachedTurkey;
  cachedTurkey = {
    stand: drawTurkey({ legs: "stand", eye: "open", tail: "fan", wing: "fold" }),
    blink: drawTurkey({ legs: "stand", eye: "closed", tail: "fan", wing: "fold" }),
    peck: drawTurkey({ legs: "stand", eye: "open", tail: "fan", wing: "fold", headDy: 2 }),
    run: (["run0", "run1", "run2", "run3"] as Legs[]).map((legs) => drawTurkey({ legs, eye: "open", tail: "fan", wing: "fold" })),
    fly: (["up", "mid", "down", "mid"] as const).map((wing) => drawTurkey({ legs: "tuck", eye: "open", tail: "fold", wing })),
    dizzy: drawTurkey({ legs: "stand", eye: "x", tail: "fan", wing: "fold" }),
  };
  return cachedTurkey;
}
